import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfigProvider, Table } from 'antd';
import type { TableColumnsType, ThemeConfig } from 'antd';
import { fontFamily } from '../../theme/tokens';
import Pagination from '../../components/Pagination';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import { PlusIcon, EditSquareIcon, TrashIcon, SearchIcon } from '../../assets/icons';
import {
  useGetTeamsQuery,
  useDeleteTeamMutation,
  teamName,
  teamMemberNames,
  teamMemberCount,
  type Team,
} from '../../features/teams/teamsApi';

const TABLE_THEME: ThemeConfig = {
  token: { colorBorderSecondary: '#e2e8f0', fontFamily },
  components: {
    Table: {
      headerBg: '#ffffff',
      headerColor: '#64748b',
      headerSplitColor: 'transparent',
      borderColor: '#e2e8f0',
      cellPaddingBlock: 16,
      cellPaddingInline: 24,
      rowHoverBg: '#f7faff',
      headerBorderRadius: 0,
      colorText: '#0f172a',
      fontSize: 14,
    },
  },
};

const ACTION_BTN =
  'flex items-center justify-center rounded-lg p-2 text-ink-secondary transition-colors hover:bg-brand-shade hover:text-brand';

function StatusPill({ active }: { active: boolean }) {
  return (
    <span
      className={[
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium',
        active ? 'border-[#bbf7d0] bg-[#dcfce7] text-[#166534]' : 'border-[#e2e8f0] bg-[#f1f5f9] text-[#475569]',
      ].join(' ')}
    >
      <span className={['size-2 rounded-full', active ? 'bg-[#22c55e]' : 'bg-[#94a3b8]'].join(' ')} />
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

const isActiveTeam = (t: Team) => (t.status || 'Active').toLowerCase() !== 'inactive';

export default function TeamsPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'' | 'active' | 'inactive'>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [teamToDelete, setTeamToDelete] = useState<Team | null>(null);

  // Debounce the search box so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(query.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const isActive = status === 'active' ? true : status === 'inactive' ? false : '';
  const { data, isFetching } = useGetTeamsQuery({ search, isActive, page, pageSize });
  const [deleteTeam, { isLoading: isDeleting }] = useDeleteTeamMutation();

  const pageRows = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = (page - 1) * pageSize;

  const clearFilters = () => {
    setQuery('');
    setStatus('');
    setPage(1);
  };

  const confirmDelete = async () => {
    if (!teamToDelete) return;
    try {
      await deleteTeam(teamToDelete.id).unwrap();
      setTeamToDelete(null);
    } catch {
      /* keep the modal open so the user can retry */
    }
  };

  const columns: TableColumnsType<Team> = [
    {
      title: 'Team Name',
      key: 'name',
      render: (_, t) => <span className="text-sm font-semibold text-[#0f172a]">{teamName(t)}</span>,
    },
    {
      title: 'User',
      key: 'users',
      render: (_, t) => {
        const names = teamMemberNames(t);
        if (names.length) return <span className="text-sm text-[#64748b]">{names.join(', ')}</span>;
        const n = teamMemberCount(t);
        return (
          <span className="text-sm text-[#94a3b8]">{n > 0 ? `${n} member${n === 1 ? '' : 's'}` : 'No users'}</span>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 180,
      align: 'center',
      render: (_, t) => <StatusPill active={isActiveTeam(t)} />,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      align: 'center',
      render: (_, t) => (
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            aria-label="Edit"
            onClick={() => navigate(`/admin/teams/${t.id}/edit`)}
            className={ACTION_BTN}
          >
            <EditSquareIcon className="size-4" />
          </button>
          <button
            type="button"
            aria-label="Delete"
            onClick={() => setTeamToDelete(t)}
            className={[ACTION_BTN, 'hover:text-red-500'].join(' ')}
          >
            <TrashIcon className="size-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold leading-9 text-ink">Teams</h1>
          <p className="text-sm text-ink-secondary">
            Group users into teams to coordinate work across the platform.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/admin/teams/new')}
          className="flex items-center justify-center gap-2 self-start rounded-md bg-brand px-4 py-2 text-sm font-medium text-white shadow-btn transition-colors hover:bg-brand-dark sm:self-auto"
        >
          <PlusIcon className="size-3.5" />
          Create Team
        </button>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-[#e2e8f0] bg-white p-5 shadow-card">
        <div className="flex flex-wrap items-end gap-6">
          <div className="flex min-w-[220px] flex-1 flex-col gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.55px] text-[#64748b]">
              Team Name
            </span>
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-[14px] -translate-y-1/2 text-[#94a3b8]" />
              <input
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(1);
                }}
                placeholder="Search..."
                className="w-full rounded-md border border-[#e2e8f0] bg-white py-2.5 pl-9 pr-3 text-sm text-ink placeholder:text-[#94a3b8] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.55px] text-[#64748b]">
              Status
            </span>
            <div className="flex items-center gap-1 rounded-md border border-[#e2e8f0]/50 bg-[#f1f5f9] p-1">
              {(['active', 'inactive'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    setStatus((prev) => (prev === s ? '' : s));
                    setPage(1);
                  }}
                  className={[
                    'rounded px-4 py-1.5 text-sm font-medium capitalize transition-colors',
                    status === s ? 'bg-brand text-white shadow-btn' : 'text-[#64748b] hover:text-ink',
                  ].join(' ')}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={clearFilters}
            className="rounded-md bg-brand-shade px-5 py-2 text-sm font-medium text-[#334155] transition-colors hover:bg-[#e2ecff]"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-[#e2e8f0] bg-white shadow-card">
        <ConfigProvider theme={TABLE_THEME}>
          <Table<Team>
            className="chunaav-table"
            columns={columns}
            dataSource={pageRows}
            rowKey="id"
            pagination={false}
            loading={isFetching}
            locale={{ emptyText: 'No teams found.' }}
            scroll={{ x: 'max-content' }}
          />
        </ConfigProvider>

        <Pagination
          rangeStart={total === 0 ? 0 : start + 1}
          rangeEnd={Math.min(start + pageSize, total)}
          total={total}
          noun="teams"
          page={page}
          totalPages={totalPages}
          onChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(v) => {
            setPageSize(v);
            setPage(1);
          }}
        />
      </div>

      <ConfirmDeleteModal
        open={teamToDelete !== null}
        title="Delete Team"
        message={
          teamToDelete ? (
            <>
              Are you sure you want to delete{' '}
              <span className="font-semibold text-ink">“{teamName(teamToDelete)}”</span>? This action
              cannot be undone.
            </>
          ) : (
            ''
          )
        }
        confirmLabel="Delete Team"
        loading={isDeleting}
        onClose={() => setTeamToDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
